// Shared scouting math — powers the pre-match scouting report AND the in-room
// target/surplus row tags. Pure functions over roster snapshots.

export const READ_POS = ['QB', 'RB', 'WR', 'TE'];

export function posTotals(players) {
  const t = { QB: 0, RB: 0, WR: 0, TE: 0 };
  (players || []).forEach((p) => { if (t[p.position] != null) t[p.position] += (p.fcValue || 0); });
  return t;
}

export const fmtK = (n) => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${Math.round(n)}`);

// The full two-team read: rows for the bars, surplus sets, swap angles, and a
// one-paragraph guess at what the other side is up to.
export function buildScout({ myPlayers, oppPlayers, myPicks, oppPicks, oppTc, oppName = 'They' }) {
  const mine = posTotals(myPlayers);
  const theirs = posTotals(oppPlayers);

  const rows = READ_POS.map((pos) => {
    const a = mine[pos] || 0;
    const b = theirs[pos] || 0;
    const max = Math.max(a, b, 1);
    const ratio = a > 0 ? b / a : (b > 0 ? 2 : 1);
    const tag = ratio < 0.72 ? 'you' : ratio > 1.38 ? 'them' : 'even'; // who's loaded
    return { pos, mine: a, theirs: b, max, tag };
  });

  const mySurplus = rows.filter((r) => r.tag === 'you').map((r) => r.pos);
  const theirSurplus = rows.filter((r) => r.tag === 'them').map((r) => r.pos);

  const myFirsts = (myPicks || []).filter((p) => p.round === 1).length;
  const theirFirsts = (oppPicks || []).filter((p) => p.round === 1).length;

  // Swap angles — crossed surpluses first, then pick imbalances.
  const angles = [];
  mySurplus.slice(0, 2).forEach((ms) => {
    const ts = theirSurplus.find((t) => t !== ms);
    if (ts) angles.push(`Your ${ms} room for their ${ts} room — the natural swap.`);
  });
  if (!angles.length && mySurplus.length && !theirSurplus.length) {
    angles.push(`You're deeper at ${mySurplus.join(' + ')} — sell from strength, ask for their best available.`);
  }
  if (theirFirsts - myFirsts >= 2) angles.push(`They hold ${theirFirsts} firsts to your ${myFirsts} — players-for-picks is live.`);
  else if (myFirsts - theirFirsts >= 2) angles.push(`You hold ${myFirsts} firsts to their ${theirFirsts} — buy a starter with picks.`);
  if (!angles.length) angles.push('No clean mismatch — win on raw value, not need.');

  // The read: what are they probably doing here?
  const rank = oppTc?.valueRank && oppTc?.numTeams ? `#${oppTc.valueRank} of ${oppTc.numTeams} in talent` : null;
  const rec = oppTc?.prevRecord ? `${oppTc.prevRecord.wins}-${oppTc.prevRecord.losses} last yr` : null;
  const facts = [rank, rec].filter(Boolean).join(' · ');
  const win = oppTc?.window || 'bubble';
  const narrative = win === 'contender'
    ? 'Win-now mode — they’ll pay up for proven starters and shop their picks. Dangle production, ask for futures.'
    : win === 'rebuilding'
      ? 'Rebuilding — they want youth and draft capital. Your veterans are exactly what they’ll bite on.'
      : 'On the bubble — value hunters with no desperation. Make even offers look like wins for them.';
  const read = `${facts ? facts + '. ' : ''}${narrative}`;

  // Faces for the report: their best at THEIR surplus spots (hunt), your best
  // at YOUR surplus spots (dangle).
  const topAt = (players, pos, n) => (players || [])
    .filter((p) => p.position === pos && p.fcValue)
    .sort((a, b) => (b.fcValue || 0) - (a.fcValue || 0))
    .slice(0, n)
    .map((p) => ({ name: p.name, sleeperId: p.sleeperId, pos: p.position, team: p.team }));
  const targets = theirSurplus.flatMap((pos) => topAt(oppPlayers, pos, 2)).slice(0, 2);
  const dangles = mySurplus.flatMap((pos) => topAt(myPlayers, pos, 2)).slice(0, 2);

  return { rows, mySurplus, theirSurplus, myFirsts, theirFirsts, angles: angles.slice(0, 3), read, window: win, windowLabel: oppTc?.windowLabel || null, targets, dangles };
}
